import { useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Icon from '../components/ui/Icon.jsx';
import SectionState from '../components/ui/SectionState.jsx';
import Pagination from '../components/ui/Pagination.jsx';
import { usePagedList } from '../hooks/usePagedList.js';
import { getVirtualProducts } from '../api/virtualApi.js';
import {
  VIRTUAL_MACHINES,
  VIRTUAL_MACHINE_LABELS,
  VIRTUAL_VALIDITY_LABELS,
  VIRTUAL_CATEGORY_LABELS,
} from '../data/virtual-products.js';
import { youtubeIdOf } from '../utils/video.js';
import { trackFilter } from '../utils/analytics.js';
import { isVirtualExpired } from '../utils/virtualAvailability.js';
import './VirtualPage.css';

const PAGE_SIZE = 8;

const TABS = [
  { value: 'all',                 label: '全部' },
  { value: VIRTUAL_MACHINES.D2,   label: 'D2 主題' },
  { value: VIRTUAL_MACHINES.D3,   label: 'D3 主題' },
  { value: VIRTUAL_MACHINES.FUN,  label: 'Fun Dart' },
];

const AVAILABILITY_TABS = [
  { value: 'active', label: '販售中' },
  { value: 'ended', label: '已結束' },
  { value: 'all', label: '全部' },
];

const MACHINE_VALUES = new Set(TABS.map(tab => tab.value));
const AVAILABILITY_VALUES = new Set(AVAILABILITY_TABS.map(tab => tab.value));

function VirtualPage() {
  const gridRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMachine = MACHINE_VALUES.has(searchParams.get('machine')) ? searchParams.get('machine') : 'all';
  const initialAvailability = AVAILABILITY_VALUES.has(searchParams.get('availability'))
    ? searchParams.get('availability') : 'active';
  const list = usePagedList(p => getVirtualProducts(p), {
    limit: PAGE_SIZE,
    initialFilters: { machine: initialMachine, availability: initialAvailability },
  });
  const { items, total, totalPages, page, setPage, filters, setFilter, loading, error, refetch } = list;
  const activeTab = filters.machine;
  const activeAvailability = filters.availability;

  const changeFilter = (key, value) => {
    setFilter(key, value);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const isDefault = (key === 'machine' && value === 'all')
        || (key === 'availability' && value === 'active');
      if (isDefault) next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  };

  // 換頁捲到第一張卡（與主題圖鑑一致），避免回到最上方標題
  const goPage = (p) => {
    setPage(p);
    requestAnimationFrame(() => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };

  return (
    <main className="vp">
      <div className="vp-inner">
        <header className="vp-head">
          <div>
            <h1 className="vp-title">虛擬商品 · 機台主題 &amp; Fun Dart</h1>
            <p className="vp-sub">
              擲鏢之外，也能擲出個性。
            </p>
          </div>
          <div className="vp-filters">
            <div className="vp-filter-group">
              <span className="vp-filter-label">機台</span>
              <div className="vp-tabs" role="tablist" aria-label="機台分類">
                {TABS.map(t => (
                  <button
                    key={t.value}
                    role="tab"
                    aria-selected={activeTab === t.value}
                    className={activeTab === t.value ? 'on' : ''}
                    onPointerDown={() => trackFilter('machine', t.value, 'virtual_products')}
                    onClick={() => changeFilter('machine', t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="vp-filter-group">
              <span className="vp-filter-label">販售狀態</span>
              <div className="vp-tabs" role="tablist" aria-label="販售狀態">
                {AVAILABILITY_TABS.map(t => (
                  <button
                    key={t.value}
                    role="tab"
                    aria-selected={activeAvailability === t.value}
                    className={activeAvailability === t.value ? 'on' : ''}
                    onPointerDown={() => trackFilter('availability', t.value, 'virtual_products')}
                    onClick={() => changeFilter('availability', t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </header>

        <div ref={gridRef} style={{ scrollMarginTop: 90 }}>
        {error ? (
          <SectionState error={error} onRetry={refetch} />
        ) : loading && items.length === 0 ? (
          <div className="vp-grid">
            {Array.from({ length: 6 }).map((_, i) => <VCardSkeleton key={i} />)}
          </div>
        ) : total === 0 ? (
          <SectionState emptyText="此分類目前沒有主題。" />
        ) : (
          <>
            <div className="vp-grid">
              {items.map(p => <VCard key={p.id} product={p} />)}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={goPage} />
          </>
        )}
        </div>
      </div>
    </main>
  );
}


// 是否尊重「減少動態」偏好（前庭敏感者不自動播放預覽影片）
function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function VCard({ product }) {
  const expired = isVirtualExpired(product);
  const handleMouseEnter = (e) => {
    if (expired || prefersReducedMotion()) return;
    const vid = e.currentTarget.querySelector('video');
    if (vid) { vid.currentTime = 0; vid.play().catch(() => {}); }
  };
  const handleMouseLeave = (e) => {
    const vid = e.currentTarget.querySelector('video');
    if (vid) { vid.pause(); vid.currentTime = 0; }
  };

  const priceFrom = product.variants?.length
    ? Math.min(...product.variants.map(v => v.price))
    : product.price;

  // 封面：第一張商品圖（後端 images 為空會 fallback thumbUrl，mock 再保險一層）；沒圖用預設底卡
  const cover = product.images?.[0] || product.thumbUrl;
  // YouTube 連結 <video> 播不了，列表卡只對直接影片檔做 hover 預覽（YouTube 進詳情頁用 iframe 播）
  // 多支影片時取第一支可播的當 hover 預覽
  const videos = (product.previewUrls?.length ? product.previewUrls : [product.previewUrl]).filter(Boolean);
  const fileVideo = videos.find(u => !youtubeIdOf(u)) || null;

  return (
    <Link
      className={`vcard${expired ? ' ended' : ''}`}
      to={`/virtual/${product.slug}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* has-video：只有真的有可播影片時 hover 才淡出封面（否則會閃空白） */}
      <div className={`vcard-preview${fileVideo ? ' has-video' : ''}`}>
        <div className="vcard-thumb">
          {cover
            ? <img className="vcard-img" src={cover} alt="" loading="lazy" />
            : <span className="vcard-glyph">🎯</span>}
        </div>
        {expired && <span className="vcard-soldout">SOLD OUT<small>登記已截止</small></span>}
        {fileVideo && (
          <video
            className="vcard-video"
            muted
            loop
            playsInline
            preload="none"
            src={fileVideo}
          />
        )}
        <div className="vcard-badges">
          <span className={`vcard-badge machine-${product.machine}`}>
            {/* 窄卡（手機）顯示短碼 D2/D3/FUN，桌機顯示全名，避免標籤過長擠掉其他 badge */}
            <span className="m-full">{VIRTUAL_MACHINE_LABELS[product.machine]}</span>
            <span className="m-short">{product.machine?.replace('_', '/')}</span>
          </span>
          {product.tags?.includes('new')     && <span className="vcard-badge new">NEW</span>}
          {product.tags?.includes('limited') && <span className="vcard-badge limit">LIMITED</span>}
        </div>
        <div className="vcard-play-hint" />
        <div className="vcard-playing">PREVIEW</div>
      </div>

      <h3>{product.name}</h3>
      <div className="vcard-meta">
        <span>{VIRTUAL_VALIDITY_LABELS[product.validity]}</span>
        <span>{VIRTUAL_CATEGORY_LABELS[product.category]}</span>
      </div>
      <div className="vcard-foot">
          <span className="vcard-price">
            <small>NT$</small>{priceFrom}{product.variants?.length > 1 && <small> 起</small>}
          </span>
          <span className="vcard-buy">
            {expired ? '查看內容' : '查看'} <Icon name="arrow" size={12} />
          </span>
      </div>
    </Link>
  );
}

function VCardSkeleton() {
  return (
    <article className="vcard">
      <div className="vcard-preview" style={{ background: 'var(--ice)', aspectRatio: '16/10', borderRadius: 10 }} />
      <div style={{ height: 16, background: 'var(--ice)', borderRadius: 4, margin: '16px 0 8px' }} />
      <div style={{ height: 12, background: 'var(--ice)', borderRadius: 4, width: '60%' }} />
    </article>
  );
}

export default VirtualPage;
